import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchUsers, getNavigateMenu, NavigationMenuItem, updateNavigateMenu, impersonateUser } from "@/services/authService";
import { Link } from "react-router-dom";
import { Settings, Mail, Phone, User as UserIcon, ToggleLeft, ToggleRight, LayoutGrid, HelpCircle, LogIn } from "lucide-react";
import { MessageSquare, Workflow, EuroIcon, MessageCircle, Smartphone } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useAuth } from "@/contexts/AuthContext";
import toast from "react-hot-toast";

const UsersList = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { user: currentUser } = useAuth();
    const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; menuId: number; currentActive: boolean } | null>(null);
    const [impersonating, setImpersonating] = useState<string | null>(null);

    const startTour = () => {
        const driverObj = driver({
            showProgress: true,
            showButtons: ['next', 'previous', 'close'],
            steps: [
                {
                    element: '#users-list-title',
                    popover: {
                        title: t('usersList'),
                        description: t('tourUsersListDesc') || 'Esta página te permite gestionar todos los usuarios del sistema. Aquí puedes ver la información de cada usuario y acceder a su configuración.',
                        side: "bottom",
                        align: 'start'
                    }
                },
                {
                    element: '#users-table',
                    popover: {
                        title: t('usersTable'),
                        description: t('tourUsersTableDesc') || 'En esta tabla se muestran todos los usuarios registrados con su información básica: nombre, correo, teléfono y si tienen permisos de administrador.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#user-settings-btn',
                    popover: {
                        title: t('userSettings'),
                        description: t('tourUserSettingsDesc') || 'Haciendo click en este botón puedes acceder a la configuración específica de cada usuario.',
                        side: "left",
                        align: 'start'
                    }
                },
                {
                    element: '#navigation-menus-section',
                    popover: {
                        title: t('navigationMenus'),
                        description: t('tourNavigationMenusDesc') || 'Esta sección te permite gestionar los menús de navegación que estarán disponibles para todos los usuarios del sistema.',
                        side: "top",
                        align: 'start'
                    }
                },
                {
                    element: '#menu-toggle-btn',
                    popover: {
                        title: t('menuActivation'),
                        description: t('tourMenuActivationDesc') || 'Usa estos botones para activar o desactivar los menús. Los menús activos aparecerán en la navegación de los usuarios.',
                        side: "left",
                        align: 'start'
                    }
                }
            ]
        });

        driverObj.drive();
    };

    const { data: users, isLoading, error } = useQuery({
        queryKey: ["users"],
        queryFn: fetchUsers,
    });

    const { data: navigationMenus, isLoading: menusLoading } = useQuery({
        queryKey: ["navigationMenus"],
        queryFn: getNavigateMenu,
    });

    const updateMenuMutation = useMutation({
        mutationFn: (item: NavigationMenuItem) => updateNavigateMenu(item),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["navigationMenus"] });
            setConfirmModal(null);
        },
    });

    const handleToggleMenu = (menu: NavigationMenuItem) => {
        setConfirmModal({
            isOpen: true,
            menuId: menu.id,
            currentActive: menu.active,
        });
    };

    const confirmToggle = () => {
        if (!confirmModal) return;

        const menu = navigationMenus?.find((m) => m.id === confirmModal.menuId);
        if (menu) {
            updateMenuMutation.mutate({
                ...menu,
                active: !menu.active,
            });
        }
    };

    const handleImpersonateUser = async (userId: string, userEmail: string) => {
        if (!currentUser?.customData?.admin) {
            toast.error(t('adminPrivilegesRequired') || 'Admin privileges required');
            return;
        }

        if (currentUser.id === userId) {
            toast.error(t('cannotImpersonateSelf') || 'Cannot impersonate yourself');
            return;
        }

        const confirmed = window.confirm(
            t('confirmImpersonation', { email: userEmail }) || 
            `Are you sure you want to login as ${userEmail}? This action will be logged for audit purposes.`
        );

        if (!confirmed) return;

        try {
            setImpersonating(userId);
            await impersonateUser(userId);
            // The function will redirect to home page after success
        } catch (error) {
            console.error('Error impersonating user:', error);
            toast.error(t('errorImpersonatingUser') || 'Failed to impersonate user');
            setImpersonating(null);
        }
    };

    const getIconComponent = (iconName: string) => {
        let Icon = LayoutGrid;

        switch (iconName) {
            case "MessageSquare":
                Icon = MessageSquare;
                break;
            case "Workflow":
                Icon = Workflow;
                break;
            case "MessageCircle":
                Icon = MessageCircle;
                break;
            case "Smartphone":
                Icon = Smartphone;
                break;
            case "EuroIcon":
                Icon = EuroIcon;
                break;
        }

        return <Icon className="h-5 w-5 text-gray-600" />;
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-gray-600">{t('loadingUsers')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-red-500">{t('errorLoadingUsers')}</p>
            </div>
        );
    }

    return (
        <section className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div id="users-list-title">
                        <h1 className="text-2xl font-bold text-gray-900">{t('usersList')}</h1>
                        <p className="text-sm text-gray-600">
                            {t('manageAndViewAllSystemUsers')}
                        </p>
                    </div>
                    <button
                 id="users-table"        onClick={startTour}
                        className="rounded-full p-2 text-gray-600 transition hover:bg-gray-100 hover:text-indigo-600"
                        title={t('startTour') || 'Iniciar tour'}
                    >
                        <HelpCircle className="h-6 w-6" />
                    </button>
                </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    {t('userName')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    {t('email')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    {t('phone')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    {t('id')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    {t('admin')}
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                    {t('actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {users?.map((user) => (
                                <tr key={user.id} className="transition hover:bg-gray-50">
                                    <td className="whitespace-nowrap px-6 py-4">
                                        <div className="flex items-center">
                                            {user.profileImageUrl ? (
                                                <img
                                                    src={user.profileImageUrl}
                                                    alt={user.name || "Usuario"}
                                                    className="h-10 w-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100">
                                                    <UserIcon className="h-5 w-5 text-indigo-600" />
                                                </div>
                                            )}
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {user.name || t('noName')}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4">
                                        <div className="flex items-center text-sm text-gray-900">
                                            <Mail className="mr-2 h-4 w-4 text-gray-400" />
                                            {user.email}
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                        {user.phone ? (
                                            <div className="flex items-center">
                                                <Phone className="mr-2 h-4 w-4 text-gray-400" />
                                                {user.phone}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">{t('notAvailable')}</span>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                        <code className="rounded bg-gray-100 px-2 py-1 text-xs">
                                            {user.id}
                                        </code>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4">
                                        {user.admin ? (
                                            <span className="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                                                {t('yes')}
                                            </span>
                                        ) : (
                                            <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800">
                                                {t('no')}
                                            </span>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-2">
                                            {currentUser?.customData?.admin && currentUser.id !== user.id && (
                                                <button
                                                    onClick={() => handleImpersonateUser(user.id, user.email)}
                                                    disabled={impersonating === user.id}
                                                    className="inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-white transition hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title={t('loginAsUser') || 'Login as this user'}
                                                >
                                                    <LogIn className="h-4 w-4" />
                                                    {impersonating === user.id ? t('loggingIn') || 'Logging in...' : t('loginAs') || 'Login As'}
                                                </button>
                                            )}
                                            <Link
                                                id="user-settings-btn"
                                                to={`/users/${user.id}/settings`}
                                                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white transition hover:bg-indigo-700"
                                            >
                                                <Settings className="h-4 w-4" />
                                                {t('settings')}
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {users?.length === 0 && (
                    <div className="py-12 text-center">
                        <p className="text-gray-500">{t('noUsersAvailable')}</p>
                    </div>
                )}
            </div>

            <div className="text-sm text-gray-500">
                {t('totalUsers')}: <span className="font-semibold">{users?.length || 0}</span>
            </div>

            {/* Menúsid="navigation-menus-section"  de Navegación */}
            <section id="navigation-menus-section" className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">{t('navigationMenus')}</h2>
                        <p className="text-sm text-gray-600">
                            {t('manageMenusAvailableForAllUsers')}
                        </p>
                    </div>
                </div>

                {menusLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <p className="text-gray-600">{t('loadingMenus')}</p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            {t('name')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            {t('icon')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            {t('link')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            {t('order')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                            {t('system')}
                                        </th>
                                        <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                                            {t('status')}
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                            {t('actions')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {navigationMenus?.map((menu) => (
                                        <tr key={menu.id} className="transition hover:bg-gray-50">
                                            <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                                                {menu.translationKey}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                                                {getIconComponent(menu.icon)}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                                                <code className="rounded bg-gray-100 px-2 py-1 text-xs">
                                                    {menu.href}
                                                </code>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                                                {menu.order}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                {menu.isSystem ? (
                                                    <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">
                                                        {t('yes')}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800">
                                                        {t('no')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-center">
                                                {menu.active ? (
                                                    <span className="inline-flex rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                                                        {t('active')}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">
                                                        {t('inactive')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                                <button id="menu-toggle-btn"
                                                    
                                                    onClick={() => handleToggleMenu(menu)}
                                                    disabled={updateMenuMutation.isPending}
                                                    className={`inline-flex w-32 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                                        menu.active
                                                            ? "bg-red-600 hover:bg-red-700"
                                                            : "bg-green-600 hover:bg-green-700"
                                                    }`}
                                                >
                                                    {menu.active ? (
                                                        <ToggleLeft className="h-4 w-4 text-white" />
                                                    ) : (
                                                        <ToggleRight className="h-4 w-4 text-white" />
                                                    )}
                                                    {menu.active ? t('deactivate') : t('activate')}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {navigationMenus?.length === 0 && (
                            <div className="py-12 text-center">
                                <p className="text-gray-500">{t('noMenusAvailable')}</p>
                            </div>
                        )}
                    </div>
                )}

                <div className="text-sm text-gray-500">
                    {t('totalMenus')}: <span className="font-semibold">{navigationMenus?.length || 0}</span>
                </div>
            </section>

            {/* Modal de Confirmación */}
            {confirmModal?.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="w-full max-w-sm rounded-lg bg-white shadow-lg">
                        <div className="px-6 py-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {t('areYouSure')}
                            </h3>
                            <p className="mt-2 text-sm text-gray-600">
                                {t('doYouWantToToggleMenu', { 
                                    action: confirmModal.currentActive ? t('deactivate').toLowerCase() : t('activate').toLowerCase() 
                                })}
                            </p>
                        </div>
                        <div className="flex gap-3 border-t border-gray-200 px-6 py-4">
                            <button
                                onClick={() => setConfirmModal(null)}
                                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={confirmToggle}
                                disabled={updateMenuMutation.isPending}
                                className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {updateMenuMutation.isPending ? t('processing') : t('confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </section>
    );
};
 
export default UsersList;